import type { TrackEntry } from "@esotericsoftware/spine-pixi-v8";
import { transition, type SpineArtboardSpec } from "@slot/presentation/rive-spine";

/** The Rive file's view model `Pirate`: the triggers the game fires, by their names. */
export const CAPTAIN_RIVE = { coinFlip: "coinFlip", win: "win", bigWin: "bigWin" } as const;

/**
 * `pirate-captain.riv`'s artboard `Pirate` (887 × 1496, v4 of 2026-10-24) as it is drawn: with 15 pixels of margin on its
 * left, where his hip hand reaches in the big win's star jump (rive-spine measured it up to 12 past the artboard's left
 * edge; nothing else leaves the artboard), and his soles, the artist's anchor (443.34, 1470 in the artboard), as the
 * skeleton's origin.
 */
export const CAPTAIN_LAYOUT = {
  artboard: { width: 902, height: 1496 },
  soles: { x: 458.34, y: 1470 },
} as const;

/*
 * The state machine `Pirate` as rive-spine converted it (v3 on 2026-10-23, v4 on 2026-10-24, the same in both): its
 * layers are tracks, its states animations. Body (track 0) loops `idle`; each trigger switches it to its one-shot over
 * 0.12 s, from the idle or from another one-shot, and the one-shot's end goes back to `idle` over 0.22 s. Face (track 1,
 * the mouth) plays `idleFace` once and holds it; each trigger switches it at once to the one-shot's face, whose end goes
 * back to `idleFace`. Blink (track 2) loops on its own. No state goes to itself, so a trigger whose one-shot plays is
 * ignored, and a trigger fired while Body mixes is dropped there (Face, switching at once, still takes it: so does the
 * Rive file).
 */
const BODY = 0;
const FACE = 1;
const BLINK = 2;
/** A trigger's switch on Body, and Body's way back to its idle at a one-shot's end. */
const TRIGGER_MIX_SECONDS = 0.12;
const RETURN_MIX_SECONDS = 0.22;

type Trigger = keyof typeof CAPTAIN_RIVE;

/**
 * The captain's artboard, its controls the Rive file's triggers. Coin flip is also his idle's variation (the user,
 * 2026-10-23): after `idleLoopsPerCoinFlip` whole idle loops in a row he flips his coin once, as its trigger does; any
 * one-shot starts the count again.
 */
export function captainArtSpec(tuning: { idleLoopsPerCoinFlip: number; frameSeconds: number }): SpineArtboardSpec {
  return {
    artboard: CAPTAIN_LAYOUT.artboard,
    origin: CAPTAIN_LAYOUT.soles,
    frameSeconds: tuning.frameSeconds,
    controls({ state }) {
      state.setAnimation(BODY, "idle", true);
      state.setAnimation(FACE, "idleFace", false);
      state.setAnimation(BLINK, "blink", true);
      const fire = (trigger: Trigger): void => {
        transition(state, BODY, trigger, TRIGGER_MIX_SECONDS, { then: { animation: "idle", loop: true, mix: RETURN_MIX_SECONDS }, trigger: true });
        transition(state, FACE, `${trigger}Face`, 0, { then: { animation: "idleFace" }, trigger: true });
      };
      let idleLoops = 0;
      state.addListener({
        start: (entry: TrackEntry) => {
          if (entry.trackIndex === BODY) idleLoops = 0;
        },
        complete: (entry: TrackEntry) => {
          // A loop of the idle Body is in (not one it mixes out of).
          if (entry.trackIndex !== BODY || entry !== state.getCurrent(BODY) || entry.animation?.name !== "idle") return;
          idleLoops += 1;
          if (idleLoops >= tuning.idleLoopsPerCoinFlip) fire("coinFlip");
        },
      });
      return { triggers: { [CAPTAIN_RIVE.coinFlip]: () => fire("coinFlip"), [CAPTAIN_RIVE.win]: () => fire("win"), [CAPTAIN_RIVE.bigWin]: () => fire("bigWin") } };
    },
  };
}
